import React from 'react'
import './BodyAssessmentMobile.content.css'
import { useDispatch, useSelector } from 'react-redux'
import { getBodyAssessmentSelector } from '../../selectors'
import { BodyAssessmentConstants } from '../../store/bodyAssessment.constants'
import BurnsRuleOfNinesContent from '../../burns/burnsRuleOfNines/content/BurnsRuleOfNines.content'
import BurnsTbsaContent from '../../burns/burnsTbsa/content/BurnsTbsa.content'
import DrainsTubesContent from '../../drainsTubes/content/DrainsTubes.content'
import ObstetricsContent from '../../obstetrics/content/Obstetrics.content'
import AssessmentFindingsComponent from '../../assessmentFindings/AssessmentFindings.component'
import { IonContent, IonFooter, IonToolbar } from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../../../components/tabs/Tabs.constants'
import ActionButtons from '../../../../../components/actionButtons/ActionButtons.component'
import { showAlert } from '../../../../../components/alert/store/alert.actions'
import BodySurveyContent from '../../bodySurvey/content/BodySurvey.content'
import { setSelectedAssessment } from '../../../store/additionalAssessments.actions'
import {
  resetBodySurveyValues,
  saveBodySurvey,
  setDefaults as setAssessmentDefaults,
  setDefaults,
} from '../../bodySurvey/store/bodySurvey.actions'
import { getBodySurveyOptionsSelector } from '../../../../options/selectors'
import { showToast } from '../../../../../components/toast/store/toast.actions'
import { ToastColor } from '../../../../../components/toast/store/toast.constants'
import { getBodySurveySelector } from '../../bodySurvey/selectors'

export interface IBodyAssessmentsMobileProps {
  summaryCount: number
}

const BodyAssessmentMobile: React.FC<IBodyAssessmentsMobileProps> = ({ summaryCount }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()

  const { selectedExam } = useSelector(getBodyAssessmentSelector)
  const bodySurvey = useSelector(getBodySurveySelector)
  const examOptions = useSelector(getBodySurveyOptionsSelector)
  const { assessmentDefaultOptions } = examOptions
  const { seq, __documentFindings, examValues, examValuesPNNVs, findings, __setDefaults, __selectedBodyParts } =
    bodySurvey

  const isNeuroActive = selectedExam === BodyAssessmentConstants.NEURO_CONDITIONS
  const isMentalActive = selectedExam === BodyAssessmentConstants.MENTAL_CONDITIONS
  const isCardioActive = selectedExam === BodyAssessmentConstants.CARDIO_CONDITIONS

  const hideFooterButtonsForExams: Array<string> = [
    BodyAssessmentConstants.BURNS_RULE_OF_NINES,
    BodyAssessmentConstants.BURNS_TBSA,
    BodyAssessmentConstants.OBSTETRICS,
    BodyAssessmentConstants.DRAINS_TUBES,
  ]
  const enable = examValues.length || examValuesPNNVs.length || findings.length || __selectedBodyParts.length > 0

  const examContent = () => (
    <>
      {selectedExam === BodyAssessmentConstants.BODY_SURVEY &&
        (__documentFindings ? <AssessmentFindingsComponent bodySurvey={bodySurvey} /> : <BodySurveyContent />)}
      {selectedExam === BodyAssessmentConstants.BURNS_RULE_OF_NINES && <BurnsRuleOfNinesContent />}
      {selectedExam === BodyAssessmentConstants.BURNS_TBSA && <BurnsTbsaContent />}
      {selectedExam === BodyAssessmentConstants.DRAINS_TUBES && <DrainsTubesContent />}
      {selectedExam === BodyAssessmentConstants.OBSTETRICS && <ObstetricsContent />}
      {(isNeuroActive || isMentalActive || isCardioActive) && <AssessmentFindingsComponent bodySurvey={bodySurvey} />}
    </>
  )

  const onSummaryBtnClick = () => {
    history.push({ pathname: TabConstants.ADDITIONAL_ASSESSMENT_SUMMARY, state: TabConstants.BODY_ASSESSMENT })
    dispatch(setSelectedAssessment(bodySurvey))
  }

  const handleDelete = () => {
    dispatch(resetBodySurveyValues())
    dispatch(saveBodySurvey())
  }

  const onClearClick = () => {
    const alertState = {
      header: t('additionalAssessments.clearBodySurvey.header'),
      message: `${t('additionalAssessments.clearBodySurvey.message')} ${seq}?`,
      buttons: [{ text: 'Cancel' }, { text: 'Ok', role: 'destructive', handler: handleDelete }],
    }
    dispatch(showAlert(alertState))
  }

  const handleDefaults = () => {
    if (assessmentDefaultOptions.length === 0) {
      dispatch(
        showToast({
          message: `${t('additionalAssessments.notifications.noDefaults')}`,
          position: 'top',
          color: ToastColor.Info,
        })
      )
      dispatch(setDefaults(false))
    } else {
      dispatch(setAssessmentDefaults())
      dispatch(setDefaults(true))
    }
  }

  const handleConfirmSave = () => {
    dispatch(saveBodySurvey())
    history.push(TabConstants.ADDITIONAL_ASSESSMENTS)
  }

  const onDoneClick = () => {
    const alertState = {
      header: t('additionalAssessments.completeAssessment.header'),
      message: `${t('additionalAssessments.completeAssessment.message')} Assessment ${seq}`,
      buttons: [{ text: 'Cancel' }, { text: 'Ok', handler: handleConfirmSave }],
    }
    dispatch(showAlert(alertState))
  }

  const setDefaultsValue = () => {
    const alertState = {
      header: t('additionalAssessments.setDefaults.header'),
      message: t('additionalAssessments.setDefaults.message'),
      buttons: [{ text: 'Cancel' }, { text: 'Ok', handler: handleDefaults }],
    }
    dispatch(showAlert(alertState))
  }

  const hideFooterButtons = !hideFooterButtonsForExams.includes(selectedExam)

  const footerButtons = [
    {
      label: `${t('additionalAssessments.Summary')} ${'(' + summaryCount + ')'}`,
      className: 'btn btn-secondary summary-btn-mobile',
      onClick: () => {
        onSummaryBtnClick()
      },
      show: true,
    },
    {
      label: t('common.buttons.Clear'),
      className: 'btn mr-8',
      color: 'danger',
      fill: 'outline',
      onClick: () => {
        onClearClick()
      },
      disabled: !enable,
      show: hideFooterButtons,
    },
    {
      label: t('additionalAssessments.bodySurvey.defaults'),
      className: 'btn btn-primary',
      onClick: () => setDefaultsValue(),
      show: !__setDefaults && hideFooterButtons,
    },
    {
      label: t('common.buttons.Done'),
      className: 'btn btn-primary',
      onClick: () => onDoneClick(),
      disabled: !enable,
      show: __setDefaults,
    },
  ]

  const filterFooterButtons = footerButtons.filter((footer) => footer.show)

  return (
    <>
      <IonContent>
        <div className='body-assessment-mobile'>{examContent()}</div>
      </IonContent>
      {!__documentFindings && (
        <IonFooter>
          <IonToolbar color='light'>
            <ActionButtons actionButtonObj={filterFooterButtons} />
          </IonToolbar>
        </IonFooter>
      )}
    </>
  )
}

export default BodyAssessmentMobile
